import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ConfirmationModal from "../Shared/confirmationModal";
import { adminEmbeddedTablePaperSx, adminDataTableHeadSx } from "../../utils/adminTableScrollSx";
import adminPaymentMethodsService from "../../services/adminPaymentMethodsService";
import { useUI } from "../Context/UIContext";

const emptyForm = () => ({ descrizione: "", urlLogo: "" });

const GestioneMetodiPagamento = () => {
  const { showError, showSuccess } = useUI();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const loadPaymentMethods = useCallback(async () => {
    setLoading(true);
    try {
      const items = await adminPaymentMethodsService.listPaymentMethods();
      setMethods(items ?? []);
    } catch (e) {
      showError(e?.response?.data ?? e?.message ?? "Errore caricamento metodi di pagamento");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const openCreate = () => {
    setSelected(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (method) => {
    setSelected(method);
    setForm({
      descrizione: method.descrizione ?? "",
      urlLogo: method.urlLogo ?? "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      await adminPaymentMethodsService.savePaymentMethod({
        id: selected?.id,
        descrizione: form.descrizione,
        urlLogo: form.urlLogo,
      });
      showSuccess(selected ? "Metodo di pagamento aggiornato" : "Metodo di pagamento creato");
      setDialogOpen(false);
      setForm(emptyForm());
      setSelected(null);
      loadPaymentMethods();
    } catch (e) {
      showError(e?.response?.data ?? e?.message ?? "Errore salvataggio metodo di pagamento");
    }
  };

  const confirmDelete = async () => {
    if (!selected?.id) {
      return;
    }
    try {
      await adminPaymentMethodsService.deletePaymentMethod(selected.id);
      showSuccess("Metodo di pagamento eliminato");
      setDeleteOpen(false);
      setSelected(null);
      loadPaymentMethods();
    } catch (e) {
      showError(e?.response?.data ?? e?.message ?? "Errore eliminazione metodo di pagamento");
    }
  };

  return (
    <Box mt={4}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" className="page-title">
          Gestione metodi di pagamento E-Commerce
        </Typography>
        <Button variant="contained" onClick={openCreate}>
          Nuovo metodo
        </Button>
      </Box>

      {/* Nessuno scroll interno: la tabella cresce e a scorrere e' la pagina */}
      <TableContainer component={Paper} elevation={0} sx={{ ...adminEmbeddedTablePaperSx, overflowX: "auto" }}>
        <Table sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
          <TableHead sx={adminDataTableHeadSx}>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Anteprima immagine</TableCell>
              <TableCell>URL immagine</TableCell>
              <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && methods.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>Nessun metodo di pagamento configurato.</TableCell>
              </TableRow>
            ) : null}
            {methods.map((method, i) => (
              <TableRow key={method.id} sx={{ backgroundColor: i % 2 === 0 ? "#fff" : "#f5f7fa" }}>
                <TableCell>{method.descrizione}</TableCell>
                <TableCell>
                  {method.urlLogo ? (
                    <Box
                      component="img"
                      src={method.urlLogo}
                      alt={method.descrizione}
                      sx={{ maxHeight: 32, maxWidth: 80, objectFit: "contain" }}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell sx={{ wordBreak: "break-all" }}>{method.urlLogo || "-"}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    sx={{ color: "#0b3d87", fontWeight: "bold", mr: 1 }}
                    onClick={() => openEdit(method)}
                  >
                    MODIFICA
                  </Button>
                  <Button
                    size="small"
                    sx={{ color: "#e00036", fontWeight: "bold" }}
                    onClick={() => {
                      setSelected(method);
                      setDeleteOpen(true);
                    }}
                  >
                    ELIMINA
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>{selected ? "Modifica metodo di pagamento" : "Nuovo metodo di pagamento"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Label"
              fullWidth
              value={form.descrizione}
              onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
            />
            <TextField
              label="URL immagine"
              fullWidth
              value={form.urlLogo}
              onChange={(e) => setForm({ ...form, urlLogo: e.target.value })}
            />
            {form.urlLogo ? (
              <Box
                component="img"
                src={form.urlLogo}
                alt="Anteprima"
                sx={{ maxHeight: 48, maxWidth: 120, objectFit: "contain", alignSelf: "flex-start" }}
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button variant="contained" onClick={handleSave}>Salva</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        title="Eliminare il metodo di pagamento?"
        subtitle="L'operazione rimuoverà anche le associazioni con i vendor."
        message={`Stai per eliminare "${selected?.descrizione ?? ""}".`}
        confirmText="ELIMINA"
      />
    </Box>
  );
};

export default GestioneMetodiPagamento;
